import { useContext, useEffect, useRef, useState } from 'react';
import { Buffer } from 'buffer';
import { Button, Box, CircularProgress } from '@mui/material';
import { CreateDealContext } from '../../../context/CreateDealContext';
import { IAutoDocuments } from '../../../types/CreateDealForm';
import { FlexBetween } from '../../../commonStyles';
import { useSelector } from 'react-redux';
import { selectProject } from '../../../features/project/projectSlice';
import { selectUser } from '../../../features/auth/authSlice';
import { useAppDispatch } from '../../../app/hooks';
import { showErrorSnackbar } from '../../../features/snackbar/snackbarSlice';
import { useNavigate } from 'react-router-dom';
import { IOption } from '../../../types/project';
import { getSalesStatusCodes } from '../../../utils/Functions';

const ReviewStep = (props: ChildProps) => {
  const { createDeal } = props;
  const storeDispatch = useAppDispatch();
  const navigate = useNavigate();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const {
    suiteInfo,
    setActiveStep,
    activeStep,
    selectedUnit,
    realtorId,
    purchaserId,
    purchaserInfo,
    apsPdf,
    totalPdf,
    selectedAps,
    noRealtor,
    realtorInfo,
    validEmails,
    autoDocuments,
    executor,
    selectedDeposit,
    coopRates,
    rental,
    pdfTemplate,
  } = useContext(CreateDealContext);
  const [downloadUrl, setDownloadUrl] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const testSendRef = useRef(false);

  useEffect(() => {
    createUrl();
  }, []);

  useEffect(() => {
    const boldSign = (params: MessageEvent) => {
      if (params.origin !== 'https://app-ca.boldsign.com') {
        return;
      }

      switch (params.data.status) {
        case 'onCreateSuccess':
          window.removeEventListener('message', boldSign);
          sendDeal(false, testSendRef.current);
          break;
        case 'onCreateFailed':
          window.removeEventListener('message', boldSign);
          break;
        }
    };

    window.addEventListener('message', boldSign);
    return () => window.removeEventListener('message', boldSign);
  }, []);

  const createTemplate = async () => {
    let iframeEle: any = document.getElementById('prepare_page');
    function onCreateClick() {
      iframeEle?.contentWindow.postMessage('onCreateClick', '*');
    }
    await onCreateClick();
  };

  const sendDeal = async (inPerson: boolean, test: boolean) => {
    if (
      selectedUnit.status === 'IP' ||
      selectedUnit.status === 'O' ||
      selectedUnit.status === 'S' ||
      getSalesStatusCodes(project).includes(selectedUnit.status)
    ) {
      storeDispatch(showErrorSnackbar('There is already an active deal for this unit'));
      navigate(`/${project._id}/dashboard/price-grid`);
      return;
    }

    if (selectedUnit.status === 'UT') {
      storeDispatch(showErrorSnackbar('Another purchaser is looking to unit transfer to this unit'));
      navigate(`/${project._id}/dashboard/price-grid`);
      return;
    }

    let apsFloorPlan = project.mergeTemplates.find((mergeTemplate: any) => mergeTemplate.name === 'floorplan');
    let apsTemplate = apsFloorPlan?.apsTemplates.find((mergeTemplate: any) => mergeTemplate.apsTemplate._id === selectedAps._id);

    if (!apsFloorPlan) return storeDispatch(showErrorSnackbar('No Floorplan for Project'));
    if (!apsTemplate) return storeDispatch(showErrorSnackbar('No Page Number Assigned on Floor Plan'));

    const deposits = suiteInfo.deposit.map((d: any) => {
      delete d.chequeDate;
      delete d.accountNumber;
      return d;
    });

    // prep signFields
    // const signFields = mergeTemplateArray.reduce((acc: IMergeArray[], curr) => acc.concat(curr.signFields || []), []);

    // prep file
    const blob = new Blob([apsPdf]);
    const file = new File([blob], 'aps.pdf', { type: 'application/pdf' });

    let autoDocumentsFile: IAutoDocuments[] = [];

    if (autoDocuments.length > 0) {
      autoDocumentsFile = await Promise.all(
        autoDocuments.map(async (autoDocument: IAutoDocuments) => {
          return {
            name: autoDocument.name,
            position: autoDocument.position ?? 'back',
          };
        })
      );
    }

    let suiteTags: string[] = [];
    if (noRealtor.noRealtor) {
      suiteTags = [...suiteInfo.tags, noRealtor.noRealtorType];
    }

    // Remove Rental from Suite Options
    let suiteOptions = suiteInfo.options.map(({ rental, ...attrs }: IOption) => attrs);

    // Add Additional Documents to end of envelope

    const args = {
      basePrice: parseFloat(selectedUnit.basePrice),
      user: user._id,
      comments: suiteInfo.comments,
      deposit: deposits,
      depositName: selectedDeposit,
      documents: [
        {
          name: 'APS',
          file,
          isAPS: true,
          type: 'APS',
          mergeTemplate: selectedAps._id,
        },
      ],
      unit: selectedUnit._id,
      salesRep: suiteInfo.salesRep._id,
      options: suiteOptions,
      project: project._id,
      purchasers: purchaserId,
      realtor: realtorId,
      tags: suiteTags,
      cc: validEmails,
      executor: executor,
      autoDocuments: autoDocumentsFile,
      inPerson: inPerson,
      coopRates: coopRates,
      rental: rental ? parseInt(rental, 10) : null,
      verifiedId: suiteInfo.verifiedId,
      test: test,
      templateId: pdfTemplate.templateId,
    };

    createDeal({ variables: args });
  };

  const createUrl = () => {
    const binaryData = Buffer.from(totalPdf, 'binary');
    const blob = new Blob([binaryData], { type: 'application/pdf' });
    const previewUrl = URL.createObjectURL(blob);
    setDownloadUrl(previewUrl);
    return null;
  };



  const downloadCreatedMerge = () => {
    const element = document.createElement('a');
    element.href = downloadUrl;
    element.target = '_blank';
    element.click();
  };

  return (
    <div>
      {!loading ? (
        <FlexBetween sx={{ my: 2 }}>
          <Button variant="contained" color="primary" onClick={() => setActiveStep(activeStep - 1)}>
            Back
          </Button>
          <Box>
            <Button sx={{ mr: 1 }} color="primary" variant="contained" onClick={() => downloadCreatedMerge()}>
              Download
            </Button>
            <Button disabled={sendLoading} sx={{ mr: 1 }} color="warning" variant="contained" onClick={() => {
              testSendRef.current = true;
              setSendLoading(true);
              createTemplate()
            }}>
              Send Test Deal
            </Button>
            <Button disabled={sendLoading} sx={{ mr: 1 }} color="success" variant="contained" onClick={() => {
              testSendRef.current = false;
              setSendLoading(true);
              createTemplate()
            }}>
              Send Deal Remotely
            </Button>
          </Box>
        </FlexBetween>
      ) : null}
      <Box sx={{ height: '768px', width: '100%' }}>
        {pdfTemplate && !loading ? (
          <iframe
            sandbox="allow-same-origin allow-scripts allow-forms"
            id="prepare_page"
            src={pdfTemplate?.createUrl}
            height="100%"
            width="100%"
            style={{ border: 0, height: '100%' }}
            title="pdfTemplate"
          />
        ) : null}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
            <Box>Sending Deal...</Box>
          </Box>
        ) : null}
      </Box>
    </div>
  );
};

interface ChildProps {
  createDeal: any;
}

export default ReviewStep;
